import { Box, List, Stack, Typography } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import type { Ordering, SortableField, SortField } from "../../../../../../../../api/types/svTypes.ts";
import { useAppDispatch } from "../../../../store/hooks/useAppDispatch.ts";
import { selectSorts, selectTabularFieldsState } from "../../../../store/selectors/fieldsStateSelectors.ts";
import { fieldsStateActions } from "../../../../store/thunks/fieldStatesThunks.ts";
import SelectSortFieldInput from "../../common/sorting/SelectSortFieldInput.tsx";
import SortFieldItem from "./SortFieldItem.tsx";

interface Props {
  availableFields: SortableField[];
  readonlyMode: boolean;
}

export const SortDropsFieldsContainer = ({ availableFields, readonlyMode }: Props) => {
  const sortingAreaValues = useSelector(selectSorts);
  const tabularFields = useSelector(selectTabularFieldsState);

  const dispatch = useAppDispatch();

  const [fields, setFields] = useState<SortField[]>([]);
  const fieldsRef = useRef(fields);
  useEffect(() => {
    fieldsRef.current = fields;
  }, [fields]);

  useEffect(() => {
    //eslint-disable-next-line @eslint-react/set-state-in-effect
    setFields(sortingAreaValues);
  }, [sortingAreaValues, setFields]);

  const onChangeSorting = useCallback(
    (field: SortField, ordering: Ordering) => {
      dispatch(fieldsStateActions.updateSortingConfig({ field, changes: { ordering } }));
    },
    [dispatch]
  );

  const onRemoveItem = useCallback(
    (field: SortField) => {
      dispatch(fieldsStateActions.removeSorting(field));
    },
    [dispatch]
  );

  const onDragMove = useCallback(
    (item: SortField, replaceItem: SortField) => {
      const values = [...fieldsRef.current];
      const fieldIndex = values.findIndex((v) => v.config.id === item.config.id);
      const toIndex = values.findIndex((v) => v.config.id === replaceItem.config.id);
      if (fieldIndex > -1) {
        values.splice(fieldIndex, 1);
        values.splice(toIndex, 0, item);
        setFields(values);
      }
    },
    [setFields]
  );

  const onEndMoveItem = useCallback(
    (field: SortField) => {
      const newIndex = fieldsRef.current.findIndex((v) => v.meta.name === field.meta.name);
      dispatch(fieldsStateActions.moveSorting({ field, newIndex }));
    },
    [dispatch]
  );

  return (
    <Box
      sx={() => ({
        display: "flex",
        flexDirection: "column",
        gap: ".5rem",
      })}
    >
      <Typography variant="subtitle2" sx={{ color: "text.primary" }}>
        Sort
      </Typography>
      <Stack
        sx={(theme) => ({
          backgroundColor: theme.palette.background.grey,
          minHeight: "90px",
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: "2px",
          p: ".5rem",
          gap: ".5rem",
        })}
      >
        {fields.length > 0 && (
          <List sx={{ p: 0, display: "flex", flexDirection: "column", gap: "5px" }}>
            {fields.map((item) => {
              const reportField = tabularFields.fields.find((f) => f.config.guid === item.config.id);
              return (
                <SortFieldItem
                  key={item.config.id}
                  sortField={item}
                  reportField={reportField}
                  onRemoveItem={onRemoveItem}
                  onChangeSorting={onChangeSorting}
                  onDragMove={onDragMove}
                  onEndMoveItem={onEndMoveItem}
                  readonlyMode={readonlyMode}
                />
              );
            })}
          </List>
        )}
        <SelectSortFieldInput availableFields={availableFields} disabled={readonlyMode} />
      </Stack>
    </Box>
  );
};

export default SortDropsFieldsContainer;
